import {useCallback, useMemo} from "react";
import {GridColDef} from "@mui/x-data-grid-pro";
import {useTranslation} from "react-i18next";
import {UserFilterDto} from "../../client/user/UserApiClient";
import {UserTranslKey} from "./UserTranslKey";
import {FilterSearchType} from "./UserSearchPanel";
import {UserDto} from "../../client/user/User";
import PageableGrid, {PageLoadSize} from "../../common/pagination/PageableGrid";
import {UserIdentitySourceFormatter} from "./UserIdentitySourceFormatter";

function buildUserGridColumns(t: (key: string) => string): GridColDef[] {
    return [
        {
            field: "login",
            headerName: t(UserTranslKey.LOGIN),
            width: 150
        },
        {
            field: "firstName",
            headerName: t(UserTranslKey.FIRST_NAME),
            width: 120,
            valueGetter: (params) => params.row.person?.firstName
        },
        {
            field: "lastName",
            headerName: t(UserTranslKey.LAST_NAME),
            width: 120,
            valueGetter: (params) => params.row.person?.lastName
        },
        {
            field: "email",
            headerName: t(UserTranslKey.EMAIL),
            width: 200,
            valueGetter: (params) => params.row.person?.email
        },
        {
            field: "identitySource",
            headerName: t(UserTranslKey.USER_IDENTITY_SOURCE),
            width: 150,
            valueFormatter: params => UserIdentitySourceFormatter.formatIdentitySource(params.value, t)
        }
    ];
}

export interface UserGridProps {
    rowsData: UserDto[];
    totalUserCount: number;
    selectedUserId?: number;

    nextPageToken?: string | null;
    userFilter: UserFilterDto;
    fetchUsers: (filter: UserFilterDto, searchType: FilterSearchType) => void;

    onSelectUser?: (user: UserDto) => void;
    onUserDoubleClick?: (userId: number) => void;
}

export default function UserGrid(props: Readonly<UserGridProps>) {
    const {rowsData, totalUserCount, selectedUserId, nextPageToken, userFilter, fetchUsers, onSelectUser, onUserDoubleClick} = props;

    const {t} = useTranslation();
    const columns = useMemo(() => buildUserGridColumns(t), [t]);

    const handleFetchMore = useCallback(() => {
        if (!nextPageToken) {
            return;
        }

        const nextPageFilter: UserFilterDto = {
            ...userFilter,
            paging: {
                maxPageSize: PageLoadSize.NEXT_PAGE_SIZE,
                pageToken: nextPageToken,
            },
        };

        fetchUsers(nextPageFilter, FilterSearchType.NEXT_PAGE_SEARCH);
    }, [nextPageToken, userFilter, fetchUsers]);

    return (
        <PageableGrid
            rowsData={rowsData}
            totalRowsCount={totalUserCount}
            columns={columns}
            onFetchMore={handleFetchMore}
            selectedRowId={selectedUserId}
            onRowSelect={onSelectUser}
            onRowDoubleClick={onUserDoubleClick}
        />
    );
}
